/**
 * Break-glass — the audited transcript reveal.
 *
 * Founder-ratified 16 Jul 2026: staff may read the transcript of a `needs_review` reference ONLY,
 * and only via an explicit, audited action. This component is that action, and every design
 * decision in it exists to keep the reveal DELIBERATE.
 *
 * *** HOW THE REVEAL IS KEPT EXPLICIT ***
 *
 *  1. It is a `useMutation`, never a `useQuery`. This is the structural guarantee, not a
 *     convention: queries fire on mount, refetch on focus, refetch on reconnect, and can be
 *     prefetched. A mutation runs when, and only when, something calls `mutate()`. The only
 *     caller is the confirm button below — the reveal cannot happen without a human pressing it.
 *  2. Nothing fires it on render, on hover, on focus, or on expanding the row. There is no
 *     prefetch, no `onMouseEnter`, no "warm the cache" call. A reveal nobody chose is a reveal
 *     that should not have happened.
 *  3. The queue row it sits in carries NO transcript data. `NeedsReviewItem` is metadata-only by
 *     design — a queue row that carried an excerpt would be a way around the chokepoint — and
 *     this panel adds none: it renders the reveal or it renders nothing.
 *  4. The API is a POST for the same reason. A GET is prefetchable, cacheable, and fired on
 *     render by well-meaning tooling — every one of those a reveal nobody chose. So this must
 *     never be wrapped in anything that auto-fires, retries, or "helpfully" re-runs.
 *  5. The reason is a closed enum (`moderation_review`) sent from a constant. There is NO
 *     free-text justification box and there must never be one: the reason lands in
 *     `audit_log.metadata`, the schema's one PII-free table — the reason it survives an erasure
 *     request — and a typed-in justification would eventually carry a name, or a quote from the
 *     very transcript being revealed, into the one place that is never scrubbed.
 *  6. The confirm names the human whose words these are, and says plainly that the act is
 *     recorded. EVERY attempt is audited — the reveal AND the denial — in the same transaction
 *     as the read, committed before any text is returned. Pressing this button writes a
 *     permanent record that you, specifically, chose to read a referee's private words. The UI
 *     should feel like that.
 *
 * Audio is explicitly out of scope. Transcript only, by design.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { breakGlassTranscript, type BreakGlassOutcome } from '@/lib/admin-api';
import { ConfirmAction } from '@/components/ConfirmAction';
import { Button } from '@/components/ui/button';

interface BreakGlassPanelProps {
  referenceRequestId: string;
  /** Whose words these are. Named in the confirm so the act is about a person, not a row. */
  refereeName: string;
}

export function BreakGlassPanel({ referenceRequestId, refereeName }: BreakGlassPanelProps) {
  const [outcome, setOutcome] = useState<BreakGlassOutcome | null>(null);
  const [failed, setFailed] = useState(false);

  const reveal = useMutation({
    // The ONLY call site of breakGlassTranscript. Reached only from the confirm button's
    // onConfirm — there is no other path to it, and there must not be.
    mutationFn: () => breakGlassTranscript(referenceRequestId),
    onSuccess: (result) => {
      setOutcome(result);
      setFailed(false);
    },
    onError: () => setFailed(true),
  });

  if (outcome?.kind === 'revealed') {
    return (
      <section
        aria-label="Revealed transcript"
        className="glass-revealed mt-3 rounded-md border border-gold/40 bg-gold-tint/25 p-4"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="font-display text-[12.5px] font-semibold text-gold-text">
            Transcript revealed
          </h4>
          <Button variant="ghost" size="sm" onClick={() => setOutcome(null)}>
            Hide
          </Button>
        </div>
        <p className="mt-1 font-body text-[11.5px] leading-relaxed text-ink-70">
          {refereeName}&rsquo;s words, in full. This reveal is recorded against your staff id.
          Hiding it does not undo that.
        </p>
        {/* The referee's verbatim speech. `whitespace-pre-wrap` so their pauses and paragraphs
            survive; no truncation, no "read more" — a partial transcript is a misleading one. */}
        <blockquote className="mt-3 max-h-[24rem] overflow-y-auto whitespace-pre-wrap rounded-md border border-line bg-surface p-3.5 font-body text-[13px] leading-relaxed text-ink">
          {outcome.transcript}
        </blockquote>
      </section>
    );
  }

  if (outcome?.kind === 'no_transcript') {
    return (
      <div
        role="status"
        className="mt-3 rounded-md border border-line-2 bg-paper-2 p-3.5 text-[12.5px] leading-relaxed text-ink-70"
      >
        <strong className="font-display font-semibold text-ink">There is no transcript.</strong>{' '}
        This request was diverted before it was ever transcribed — nothing was recorded, or the
        pipeline failed before that step. This is not a permissions problem: your read was allowed,
        and it has been recorded. There is simply nothing to see.
      </div>
    );
  }

  if (outcome?.kind === 'denied') {
    return (
      <div
        role="alert"
        className="mt-3 rounded-md border border-coral-tint bg-coral-tint/40 p-3.5 text-[12.5px] leading-relaxed text-ink-70"
      >
        <strong className="font-display font-semibold text-ink">Read refused.</strong> Break-glass
        covers the transcript of a reference sitting in <em>needs review</em>, and nothing else. The
        attempt has been recorded. If you believe this is wrong, escalate — do not retry.
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-gold/40 bg-gold-tint/20 p-3.5">
      <h4 className="font-display text-[12.5px] font-semibold text-gold-text">
        Break glass: read the transcript
      </h4>
      <p className="mt-1 max-w-[62ch] font-body text-[11.5px] leading-relaxed text-ink-70">
        The queue shows you what the pipeline flagged, never what was said. To read {refereeName}
        &rsquo;s actual words you have to break glass — a deliberate act, recorded permanently
        against your staff id, whether or not it is allowed.
      </p>

      {failed && (
        <p
          role="alert"
          className="mt-2 border-l-2 border-coral-deep pl-2 text-[11.5px] font-medium text-ink"
        >
          That didn&rsquo;t go through. Nothing was revealed. Try again in a moment.
        </p>
      )}

      <div className="mt-3">
        <ConfirmAction
          label="Break glass"
          triggerVariant="gold"
          triggerSize="sm"
          title={`Read ${refereeName}'s transcript?`}
          confirmLabel="Yes — break glass"
          confirmVariant="gold"
          pending={reveal.isPending}
          onConfirm={() => reveal.mutate()}
        >
          <p>
            You are about to read what {refereeName} said in confidence, believing it would be
            turned into a letter and read by the person they were speaking about &mdash; not by us.
          </p>
          <p className="mt-1.5">
            This writes a permanent audit record naming you, this reference, and the reason
            (moderation review). Do it because you need it to make a decision here, and for no other
            reason.
          </p>
        </ConfirmAction>
      </div>
    </div>
  );
}
